// Lazy-loaded Supabase client.
//
// The full @supabase/supabase-js SDK (~50 KB gzipped, ~190 KB parsed)
// is NOT included in the initial bundle. It is dynamically imported the
// first time any property of `supabase` is accessed (e.g.
// `supabase.functions.invoke(...)`), which only happens inside event
// handlers / async flows on chat, contact, CMS, and form pages — never
// during initial paint of marketing pages.
//
// Existing call sites continue to work unchanged:
//   import { supabase } from "@/integrations/supabase/client";
//   await supabase.functions.invoke("foo", { body: {...} });

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = "https://lpofkkjpiwfbmwqpvfqs.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imxwb2Zra2pwaXdmYm13cXB2ZnFzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAwMDU3NDEsImV4cCI6MjA2NTU4MTc0MX0.B8o3eQWA0JyE1kyUDMM4A-FQAbO1T8vB1eIvNLsTYxc";

let clientPromise: Promise<SupabaseClient<Database>> | null = null;
let clientInstance: SupabaseClient<Database> | null = null;

/**
 * Async getter — resolves the real Supabase client, loading the SDK
 * chunk on first call. Safe to call from async code where awaiting is
 * cheap. Subsequent calls reuse the same instance.
 */
export const getSupabase = async (): Promise<SupabaseClient<Database>> => {
  if (clientInstance) return clientInstance;
  if (!clientPromise) {
    clientPromise = import('@supabase/supabase-js').then(({ createClient }) => {
      clientInstance = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
      return clientInstance;
    });
  }
  return clientPromise;
};

/**
 * Backwards-compatible Proxy. All existing call sites use the pattern
 * `supabase.functions.invoke(...)` — a chain of property reads ending
 * in a method call inside an async handler. This Proxy intercepts the
 * top-level read (`functions`), returns a sub-Proxy, and when the leaf
 * method (`invoke`) is finally called it awaits the real client and
 * forwards the call. The returned promise is awaited by the caller as
 * normal.
 *
 * Limitations: only call patterns that end in a method invocation work
 * (which matches every current consumer). Direct property reads like
 * `supabase.auth.user` would NOT work and would need to migrate to
 * `await getSupabase()`. None exist today.
 */
const makeMethodProxy = (path: string[]): unknown =>
  new Proxy(function () { /* placeholder */ }, {
    get(_target, prop: string) {
      return makeMethodProxy([...path, prop]);
    },
    apply: async (_target, _thisArg, args) => {
      const client = await getSupabase();
      // Walk the property path on the real client.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let ctx: any = client;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let parent: any = client;
      for (const key of path) {
        parent = ctx;
        ctx = ctx[key];
      }
      if (typeof ctx !== 'function') {
        throw new Error(`supabase.${path.join('.')} is not a function`);
      }
      return ctx.apply(parent, args);
    },
  });

export const supabase = new Proxy({} as SupabaseClient<Database>, {
  get(_target, prop: string) {
    return makeMethodProxy([prop]);
  },
});
